import { clsx } from 'clsx';

import type { FC } from 'react';

interface Props {
  /**
   * Admin booking freezes its slots while a write is in flight. Optional, so
   * member callers keep an always-enabled button.
   */
  disabled?: boolean;
  label: string;
  selected: boolean;
  onClick: () => void;
}

/**
 * The booking time choice, shared by every scheduler that offers times.
 *
 * MEN-3990: this is the single source of the Figma treatment (file
 * `t3BrPD8G21GljDbYsf4AXN`, node `16264:33105`). Before this ticket the admin
 * page, the member biweekly page and this component each drew their own
 * version, and all three had drifted apart — different outline greys, different
 * selected treatments, different type. Changing one fixed nothing.
 *
 * The contract, in this repo's tokens:
 *
 * | | unselected | selected |
 * |---|---|---|
 * | fill | white | `sage-200` #EDF2F0 |
 * | outline | 1px `cement-200` #E3E3E3 | 2px `sage-400` #BDCCBE |
 *
 * plus `components/button-medium` type — 13px Bold, 0.4px tracking — in a 28px
 * line box with 4px padding, giving the Figma's 38px row.
 *
 * `border-solid` is load-bearing rather than decorative: this repo runs
 * Tailwind with `preflight: false`, so the width and colour utilities leave
 * `border-style` at the UA default, which for a <button> is `outset` — a bevel,
 * not the flat outline the Figma specifies.
 */
export const MentoCalTimeSlotButton: FC<Props> = ({
  disabled = false,
  label,
  selected,
  onClick,
}) => (
  <button
    aria-label={label}
    aria-pressed={selected}
    className={clsx(
      'w-full shrink-0 rounded-lg border-solid px-3 py-1 text-center text-[13px] font-bold leading-7 tracking-[0.4px] text-cement-500',
      selected
        ? 'border-2 border-sage-400 bg-sage-200'
        : 'border border-cement-200 bg-white',
    )}
    disabled={disabled}
    type="button"
    onClick={onClick}
  >
    {label}
  </button>
);
